import _ from 'lodash';
import { useOnboarding } from '../contexts/onboarding.context';
import useAuthentication from './authentication.hook';
import { mobilizeiUsersService } from '../services/mobilizei/users.service';
import { APP_AUTH_TOKEN } from '../utils/constants';
import ecommercePlatforms from '../utils/ecommercePlatforms';
import { normalizeUrl } from '../utils/url';

// The merchant who authorized the app on his platform, on the onboarding or on the
// platform's own admin, comes back with the platform credentials on the onboarding data.
export const hasPlatformAuthorization = data => !!data.ecommercePlatformToken || !!data.storeApiUsername;

// On the platforms that were not asked for the store url, it only arrives with the
// authorization, so the paths derived from it are built from what the platform sent.
export const buildAuthorizedStorePayload = data => {
    const platform = ecommercePlatforms.getById(data.ecommercePlatformId);
    // Falls back to the raw value: a url that came from the platform itself is
    // kept as it is even when it is not in the shape the field would accept.
    const storeUrl = normalizeUrl(data.storeUrl) || (data.storeUrl || '').trim().replace(/\/+$/, '');

    return {
        ...data,
        storeUrl,
        storeAccountUrl: data.storeAccountUrl || storeUrl + (platform?.storeAccountUrl || ''),
        storeCartUrl: data.storeCartUrl || storeUrl + (platform?.storeCartUrl || '')
    };
}

export default function useCreateStore() {
    const { setData, setTheme } = useOnboarding();
    const { setToken, fetchUser } = useAuthentication();

    // Creates the store. The access token returned here is the one carrying the new
    // store claims, so the next steps can reach the store. It resolves once the user
    // carrying the new store is in place, since the next steps read the store from
    // the user and would render once with the previous one otherwise.
    const createStore = (payload) => {
        // The onboarding data keeps every field it may collect, but the API rejects
        // empty strings on the optional ones (e.g. the storeId of a platform that
        // does not have one), so they are left out.
        const dataToPost = _.omitBy(payload, value => value === '' || _.isNil(value));

        return mobilizeiUsersService.createStore(dataToPost)
            .then(response => {
                // The platform credentials are only needed until the store is created,
                // so they are dropped instead of being kept on the local storage.
                setData((prev) => ({
                    ...prev,
                    ecommercePlatformToken: '',
                    ecommercePlatformRefreshToken: '',
                    storeApiUsername: '',
                    storeApiPassword: '',
                    storeApiUrl: '',
                    storeAuthCode: '',
                    pointSaleId: '',
                    partnerId: ''
                }));

                if (response?.token) {
                    localStorage.setItem(APP_AUTH_TOKEN, response.token);
                    setToken(response.token);
                }
                if (response?.theme) setTheme(response.theme);

                return fetchUser();
            });
    }

    return { createStore };
}
